import React from 'react';
import styled from 'styled-components';
import { Typography, Button, Segmented } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import DropZone from './DropZone';

const { Text } = Typography;

export const LAYOUT_CONFIGS = {
  '2x3': { cols: 2, rows: 3, zones: 6 },
  '1x3': { cols: 1, rows: 3, zones: 3 },
  '3x2': { cols: 3, rows: 2, zones: 6 },
  '2x2': { cols: 2, rows: 2, zones: 4 },
};

const LAYOUT_OPTIONS = [
  { label: '2×3', value: '2x3' },
  { label: '1×3', value: '1x3' },
  { label: '3×2', value: '3x2' },
  { label: '2×2', value: '2x2' },
];

// Timeframe label mapping
const TIMEFRAME_LABELS = {
  '0-3months': '0-3 Months',
  '3-6months': '3-6 Months',
  '6-12months': '6-12 Months',
  'custom': 'Custom Timeframe'
};

// Calculate page summary (timeframe + total budget)
const getPageSummary = (pageNum, placements) => {
  const pagePlacements = placements.filter(p => p.page_number === pageNum);
  if (pagePlacements.length === 0) {
    return { timeframe: null, total: 0, label: '' };
  }

  const timeframe = pagePlacements[0].timeframe;
  const customLabel = pagePlacements[0].timeframe_custom;
  const total = pagePlacements.reduce((sum, p) => {
    const budget = p.variable_values?.budget || '0';
    const numericBudget = parseFloat(budget.replace(/[^0-9.]/g, ''));
    return sum + (isNaN(numericBudget) ? 0 : numericBudget);
  }, 0);

  return {
    timeframe,
    total,
    label: timeframe === 'custom' ? (customLabel || 'Custom') : TIMEFRAME_LABELS[timeframe]
  };
};

const CanvasContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg || '1.5rem'};
`;

const PageCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.xl || '12px'};
  padding: ${({ theme }) => theme.spacing.lg || '1.5rem'};
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.md || '1rem'};
  padding-bottom: ${({ theme }) => theme.spacing.sm || '0.5rem'};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const PageTitle = styled(Text)`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.accent} !important;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols}, 1fr);
  grid-template-rows: repeat(${({ $rows }) => $rows}, 1fr);
  gap: ${({ theme }) => theme.spacing.sm || '0.5rem'};
  min-height: 360px;
`;

const AddPageButton = styled(Button)`
  border-style: dashed;
  height: 48px;
`;

const PageTimelineHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  margin: -1.5rem -1.5rem 1.5rem -1.5rem;
  background: ${({ theme }) => theme.colors.surfaceActive};
  border-bottom: 2px solid ${({ theme }) => theme.colors.accent};
  border-radius: 12px 12px 0 0;
`;

const TimelineText = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.accent};
`;

const BudgetTotal = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: 18px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.success};
`;

const RecommendationCanvas = ({
  pages,
  placements,
  onRemovePlacement,
  onAddPage,
  onRemovePage,
  maxPages = 3,
  layout = '2x3',
  onLayoutChange,
}) => {
  const pageCount = pages.length;
  const config = LAYOUT_CONFIGS[layout] || LAYOUT_CONFIGS['2x3'];

  return (
    <CanvasContainer>
      {/* Layout selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
        <Text style={{ color: 'var(--odin-text-secondary)', fontSize: '13px', flexShrink: 0 }}>Grid layout:</Text>
        <Segmented
          value={layout}
          onChange={onLayoutChange}
          options={LAYOUT_OPTIONS}
          size="small"
        />
      </div>

      {pages.map((pageNum) => {
        const summary = getPageSummary(pageNum, placements);

        return (
          <PageCard key={pageNum}>
            {/* Timeline header - only show if page has cards */}
            {summary.timeframe && (
              <PageTimelineHeader>
                <TimelineText>{summary.label}</TimelineText>
                <BudgetTotal>
                  £{summary.total.toLocaleString('en-GB', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  })}
                </BudgetTotal>
              </PageTimelineHeader>
            )}

            {/* Page header */}
            <PageHeader>
              <PageTitle>
                Recommendations &mdash; Page {pageNum}
              </PageTitle>
              {pageCount > 1 && (
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => onRemovePage(pageNum)}
                >
                  Remove
                </Button>
              )}
            </PageHeader>

            {/* Grid */}
            <Grid $cols={config.cols} $rows={config.rows}>
              {Array.from({ length: config.zones }, (_, i) => i + 1).map((zone) => {
                const placement = placements.find(
                  (p) => p.page_number === pageNum && p.zone_number === zone
                );
                return (
                  <DropZone
                    key={`${pageNum}-${zone}`}
                    pageNumber={pageNum}
                    zoneNumber={zone}
                    placement={placement}
                    onRemove={onRemovePlacement}
                  />
                );
              })}
            </Grid>
          </PageCard>
        );
      })}

      {pageCount < maxPages && (
        <AddPageButton
          block
          icon={<PlusOutlined />}
          onClick={onAddPage}
        >
          Add Page {pageCount + 1} (max {maxPages})
        </AddPageButton>
      )}
    </CanvasContainer>
  );
};

export default React.memo(RecommendationCanvas);
